import { orderBy } from 'lodash';
import { Injectable } from '@angular/core';
import { Action, createSelector, Selector, State, StateToken, type StateContext } from '@ngxs/store';
import * as FlatModels from '@medsurf/flat-models';
import * as FlatActions from '@medsurf/flat-actions';
import { Undoable } from '@medsurf/flat-decorators';
import * as FlatStates from '../../internal';
import { HistoryEntityBaseState } from '../base/history-entity.base.state';
import { TranslateService } from "@ngx-translate/core";

export const IMAGE_ENTITY_TOKEN = new StateToken<ImageEntityStateModel>(FlatModels.EntityControlModels.EntityQueryType.enum.IMAGE_ENTITY);

export interface ImageEntityStateModel extends FlatModels.HistoryControlEntityStatesModels.HistoryControlEntityStateModel<FlatModels.ImageEntityModels.Image> {
  imageIndex: number;
}

@State<ImageEntityStateModel>({
  name: IMAGE_ENTITY_TOKEN,
  defaults: {
    imageIndex: 0,
    snapshots: {},
    entities: {},
  }
})
@Injectable()
export class ImageEntityState extends HistoryEntityBaseState<FlatModels.ImageEntityModels.Image, ImageEntityStateModel> {
  //<editor-fold desc="Selectors">

  @Selector([ImageEntityState])
  static override typedEntities$(state: ImageEntityStateModel): { [id: string]: FlatModels.ImageEntityModels.Image } {
    return HistoryEntityBaseState.typedEntities$(state);
  }
  
  @Selector([ImageEntityState])
  static imageIndex$(state: ImageEntityStateModel) {
    return state.imageIndex;
  }

  @Selector([ImageEntityState.typedEntities$])
  static override entities$(typedEntities: { [id: string]: FlatModels.ImageEntityModels.Image }) {
    return HistoryEntityBaseState.entities$(typedEntities);
  }

  @Selector([ImageEntityState.typedEntities$])
  static override entity$(typedEntities: { [id: string]: FlatModels.ImageEntityModels.Image }) {
    return HistoryEntityBaseState.entity$(typedEntities);
  }

  @Selector([ImageEntityState.typedEntities$])
  static override entitiesByIds$(typedEntities: { [id: string]: FlatModels.ImageEntityModels.Image }) {
    return HistoryEntityBaseState.entitiesByIds$(typedEntities);
  }

  @Selector([
    ImageEntityState.typedEntities$,
    FlatStates.SlideLayerEntityState.currentSelectedSlideLayers$
  ])
  static allCurrentSelectedSlideImages$(typedEntities: { [id: string]: FlatModels.ImageEntityModels.Image },
                                        currentSelectedSlideLayers: FlatModels.LayerEntityModels.Layer[]) {
    if (Array.isArray(currentSelectedSlideLayers)) {
      return currentSelectedSlideLayers.reduce((arr, layer) => {
        if (layer.images) {
          arr.push(...layer.images);
        }
        return arr;
      }, [] as string[]).reduce((arr, id) => {
        const entity = typedEntities[id];
        if (entity) {
          arr.push(entity);
        }
        return arr;
      }, [] as FlatModels.ImageEntityModels.Image[]);
    }
    return [];
  }

  @Selector([
    ImageEntityState.typedEntities$,
    FlatStates.SlideLayerEntityState.currentSelectedSlideLayer$
  ])
  static currentSelectedTypedSlideImages$(typedEntities: { [id: string]: FlatModels.ImageEntityModels.Image },
                                          currentSelectedSlideLayer: FlatModels.LayerEntityModels.Layer | null) {
    if (currentSelectedSlideLayer?.images) {
      return currentSelectedSlideLayer.images.reduce((arr, id) => {
        const entity = typedEntities[id];
        if (entity) {
          arr[entity.id] = entity;
        }
        return arr;
      }, {} as { [id: string]: FlatModels.ImageEntityModels.Image });
    }
    return {};
  }

  @Selector([ImageEntityState.currentSelectedTypedSlideImages$])
  static currentSelectedSlideImages$(currentSelectedTypedSlideImages: { [id: string]: FlatModels.ImageEntityModels.Image }) {
    return Object.values(currentSelectedTypedSlideImages);
  }
  
  @Selector([ImageEntityState.currentSelectedSlideImages$])
  static slideImagesArray$(currentSelectedTypedSlideImages: FlatModels.ImageEntityModels.Image[]) {
    return currentSelectedTypedSlideImages.reduce((arr, image) => {
      arr.push({
        value: 'sequence-' + (image.sequenceNumber - 1),
        name: '# ' + image.sequenceNumber
      });
      return arr;
    }, [] as {value: string, name: string}[]);
  }
  
  @Selector([
    ImageEntityState.allCurrentSelectedSlideImages$,
    FlatStates.SlideLayerEntityState.typedEntities$
  ])
  static currentSelectedSlideImagesArray$(currentSelectedTypedSlideImages: FlatModels.ImageEntityModels.Image[],
                                          typedLayers: {[id: string]: FlatModels.LayerEntityModels.Layer}) {
    return (translate: TranslateService,
            imageIds: string[]) => {
      return currentSelectedTypedSlideImages.reduce((arr, image) => {
        const entry = arr.find(a => a.value === image.layer);
        if (entry) {
          entry.items.push({
            value: image.id,
            name: '# ' + image.sequenceNumber,
            inactive: imageIds.includes(image.id) && imageIds.length < 2
          });
        } else {
          arr.push({
            value: image.layer,
            name: translate.instant(('enum.layer.' + (typedLayers[image.layer].window || FlatModels.LayerEntityModels.Window.null.toString()))),
            inactive: true,
            items: [{
              value: image.id,
              name: '# ' + image.sequenceNumber + (typedLayers[image.layer].window ? (' (' + typedLayers[image.layer].window + ')') : ''),
              inactive: imageIds.includes(image.id) && imageIds.length < 2
            }]
          });
        }
        return arr;
      }, [] as { value: string, name: string, inactive: boolean, items: { value: string, name: string, inactive: boolean }[] }[]);
    };
  }

  @Selector([ImageEntityState.currentSelectedSlideImages$])
  static currentSelectedTypedSlideValidImages$(currentSelectedSlideImages: FlatModels.ImageEntityModels.Image[]) {
    if (currentSelectedSlideImages) {
      return currentSelectedSlideImages.reduce((arr, image) => {
        if (!!image.media !== undefined) {
          arr[image.id] = image;
        }
        return arr;
      }, {} as { [id: string]: FlatModels.ImageEntityModels.Image });
    }
    return {};
  }

  @Selector([ImageEntityState.currentSelectedSlideImages$])
  static currentSelectedSlideValidImages$(currentSelectedSlideImages: FlatModels.ImageEntityModels.Image[]) {
    return Object.values(currentSelectedSlideImages);
  }

  @Selector([ImageEntityState.currentSelectedSlideValidImages$])
  static currentSelectedSlideHasSequenceControl$(currentSelectedSlideValidImages: FlatModels.ImageEntityModels.Image[]) {
    return currentSelectedSlideValidImages.length > 1;
  }

  @Selector([ImageEntityState.currentSelectedSlideValidImages$])
  static currentSelectedSlideImagesMap$(currentSelectedSlideValidImages: FlatModels.ImageEntityModels.Image[]) {
    return orderBy(currentSelectedSlideValidImages, (a) => a.sequenceNumber || 0, 'asc').map(i => i.id);
  }

  @Selector([
    ImageEntityState.currentSelectedSlideValidImages$,
    ImageEntityState.currentSelectedSlideImagesMap$
  ])
  static currentSelectedImagesDefaultIndex$(currentSelectedSlideValidImages: FlatModels.ImageEntityModels.Image[],
                                            currentSelectedSlideImagesMap: string[]) {
    if (currentSelectedSlideValidImages.length === 0) {
      return -1;
    }
    const defaultImage = currentSelectedSlideValidImages.find(image => image.default);
    if (!defaultImage) {
      return 0;
    }
    const defaultIndex = currentSelectedSlideImagesMap.indexOf(defaultImage.id);
    return defaultIndex !== -1 ? defaultIndex : 0;
  }

  @Selector([
    ImageEntityState.currentSelectedTypedSlideValidImages$,
    ImageEntityState.currentSelectedSlideImagesMap$,
    ImageEntityState.imageIndex$
  ])
  static currentSelectedSlideImage$(currentSelectedTypedSlideValidImages: { [id: string]: FlatModels.ImageEntityModels.Image },
                                    currentSelectedSlideImagesMap: string[],
                                    imageIndex: number) {
    if (Number.isInteger(imageIndex) && currentSelectedSlideImagesMap) {
      return currentSelectedTypedSlideValidImages[currentSelectedSlideImagesMap[imageIndex]];
    }
    return null;
  }
  
  @Selector([ImageEntityState.currentSelectedSlideImage$])
  static currentSelectedSlideImageAnnotationOptions$(currentSelectedSlideImage: FlatModels.ImageEntityModels.Image) {
    return (annotationOptionArray: { value: string, name: string}[],
            image?: FlatModels.ImageEntityModels.Image) => {
      if (!image) {
        image = currentSelectedSlideImage;
      }
      return annotationOptionArray.reduce((arr, annotationOption) => {
        if (image && !(image.annotations || []).includes(annotationOption.value)) {
          arr.push({
            value: annotationOption.value,
            name: annotationOption.name
          });
        }
        return arr;
      }, [] as { value: string, name: string }[]);
    };
  }
  
  @Selector([ImageEntityState.currentSelectedSlideImage$])
  static currentSelectedSlideImagePoiOptions$(currentSelectedSlideImage: FlatModels.ImageEntityModels.Image) {
    return (poiOptionArray: { value: string, name: string}[], image?: FlatModels.ImageEntityModels.Image) => {
      if (!image) {
        image = currentSelectedSlideImage;
      }
      return poiOptionArray.reduce((arr, poiOption) => {
        if (image && !(image.pois || []).includes(poiOption.value)) {
          arr.push({
            value: poiOption.value,
            name: poiOption.name
          });
        }
        return arr;
      }, [] as { value: string, name: string }[]);
    };
  }

  @Selector([ImageEntityState.currentSelectedSlideImage$])
  static currentSelectedSlideImageHasAnnotations$(currentSelectedSlideImage: FlatModels.ImageEntityModels.Image) {
    return currentSelectedSlideImage && currentSelectedSlideImage.annotations && currentSelectedSlideImage.annotations.length > 0 || false;
  }
  
  @Selector([ImageEntityState.currentSelectedSlideImages$])
  static isPoiInCurrentSelectedSlideImages$(currentSelectedSlideImages: FlatModels.ImageEntityModels.Image[]) {
    return (poi: FlatModels.PointOfInterestEntityModels.PointOfInterest) => {
      for (const image of currentSelectedSlideImages) {
        if (image.pois.includes(poi.id)) {
          return true;
        }
      }
      return false;
    }
  }
  
  @Selector([ImageEntityState.currentSelectedSlideImages$])
  static isAnnotationInCurrentSelectedSlideImages$(currentSelectedSlideImages: FlatModels.ImageEntityModels.Image[]) {
    return (annotation: FlatModels.AnnotationEntityModels.AnnotationEntityType) => {
      for (const image of currentSelectedSlideImages) {
        if (image.annotations.includes(annotation.id)) {
          return true;
        }
      }
      return false;
    }
  }
  
  @Selector([ImageEntityState.allCurrentSelectedSlideImages$])
  static imagesByAnnotation$(images: FlatModels.ImageEntityModels.Image[]) {
    return (id: string) => {
      return images.reduce((arr, image) => {
        if (image.annotations.includes(id)) {
          arr.push(image);
        }
        return arr;
      }, [] as FlatModels.ImageEntityModels.Image[]);
    }
  }
  
  @Selector([ImageEntityState.allCurrentSelectedSlideImages$])
  static imageIdsByAnnotation$(images: FlatModels.ImageEntityModels.Image[]) {
    return (id: string) => {
      return images.reduce((arr, image) => {
        if (image.annotations.includes(id)) {
          arr.push(image.id);
        }
        return arr;
      }, [] as string[]);
    };
  }
  
  @Selector([ImageEntityState.allCurrentSelectedSlideImages$])
  static imagesByPoi$(images: FlatModels.ImageEntityModels.Image[]) {
    return (id: string) => {
      return images.reduce((arr, image) => {
        if (image.pois.includes(id)) {
          arr.push(image);
        }
        return arr;
      }, [] as FlatModels.ImageEntityModels.Image[]);
    }
  }
  
  @Selector([ImageEntityState.allCurrentSelectedSlideImages$])
  static imageIdsByPoi$(images: FlatModels.ImageEntityModels.Image[]) {
    return (id: string) => {
      return images.reduce((arr, image) => {
        if (image.pois.includes(id)) {
          arr.push(image.id);
        }
        return arr;
      }, [] as string[]);
    };
  }
  
  static imagesWithAnnotation$(annotationId: string) {
    return createSelector([ImageEntityState.entities$], (images: FlatModels.ImageEntityModels.Image[]) => {
      return images.reduce((arr, image) => {
        if (image.annotations.includes(annotationId)) {
          arr.push(image);
        }
        return arr;
      }, [] as FlatModels.ImageEntityModels.Image[]);
    });
  }
  
  static imagesWithPoi$(poiId: string) {
    return createSelector([ImageEntityState.entities$], (images: FlatModels.ImageEntityModels.Image[]) => {
      return images.reduce((arr, image) => {
        if (image.pois.includes(poiId)) {
          arr.push(image);
        }
        return arr;
      }, [] as FlatModels.ImageEntityModels.Image[]);
    });
  }

  //</editor-fold>

  //<editor-fold desc="Actions">

  //<editor-fold desc="Insertion">

  @Action(FlatActions.ImageEntityActions.GetEntitySuccess)
  override getEntitySuccess(state: StateContext<ImageEntityStateModel>,
                            action: FlatActions.ImageEntityActions.GetEntitySuccess) {
    return super.getEntitySuccess(state, action);
  }

  @Action(FlatActions.ImageEntityActions.GetEntitiesSuccess)
  override getEntitiesSuccess(state: StateContext<ImageEntityStateModel>,
                              action: FlatActions.ImageEntityActions.GetEntitiesSuccess) {
    return super.getEntitiesSuccess(state, action);
  }

  @Action(FlatActions.ImageEntityActions.ModifyEntitySuccess)
  override modifyEntitySuccess(state: StateContext<ImageEntityStateModel>,
                               action: FlatActions.ImageEntityActions.ModifyEntitySuccess) {
    return super.modifyEntitySuccess(state, action);
  }

  @Action(FlatActions.ImageEntityActions.ModifyEntitiesSuccess)
  override modifyEntitiesSuccess(state: StateContext<ImageEntityStateModel>,
                                 action: FlatActions.ImageEntityActions.ModifyEntitiesSuccess) {
    return super.modifyEntitiesSuccess(state, action);
  }

  //</editor-fold>

  //<editor-fold desc="Edit">

  @Action(FlatActions.ImageEntityActions.AddEntity)
  @Undoable(
    FlatActions.ImageEntityActions.AddEntity,
    IMAGE_ENTITY_TOKEN,
    FlatModels.EntityControlModels.HistorySnapshotItemModifierType.enum.ADD
  )
  override addEntity(state: StateContext<ImageEntityStateModel>,
                     action: FlatActions.ImageEntityActions.AddEntity) {
    return super.addEntity(state, action);
  }

  @Action(FlatActions.ImageEntityActions.UpdateEntity)
  @Undoable(
    FlatActions.ImageEntityActions.UpdateEntity,
    IMAGE_ENTITY_TOKEN,
    FlatModels.EntityControlModels.HistorySnapshotItemModifierType.enum.UPDATE
  )
  override updateEntity(state: StateContext<ImageEntityStateModel>,
                        action: FlatActions.ImageEntityActions.UpdateEntity) {
    return super.updateEntity(state, action);
  }

  @Action(FlatActions.ImageEntityActions.RemoveEntity)
  @Undoable(
    FlatActions.ImageEntityActions.RemoveEntity,
    IMAGE_ENTITY_TOKEN,
    FlatModels.EntityControlModels.HistorySnapshotItemModifierType.enum.REMOVE
  )
  override removeEntity(state: StateContext<ImageEntityStateModel>,
                        action: FlatActions.ImageEntityActions.RemoveEntity) {
    return super.removeEntity(state, action);
  }

  //</editor-fold>

  //<editor-fold desc="Snapshot">

  @Action(FlatActions.ImageEntityActions.UndoSnapshot)
  override undoSnapshot(state: StateContext<ImageEntityStateModel>,
                        action: FlatActions.ImageEntityActions.UndoSnapshot) {
    return super.undoSnapshot(state, action);
  }

  @Action(FlatActions.ImageEntityActions.RedoSnapshot)
  override redoSnapshot(state: StateContext<ImageEntityStateModel>,
                        action: FlatActions.ImageEntityActions.RedoSnapshot) {
    return super.redoSnapshot(state, action);
  }

  @Action(FlatActions.ImageEntityActions.CleanUpSnapshots)
  override cleanUpSnapshots(state: StateContext<ImageEntityStateModel>,
                            action: FlatActions.ImageEntityActions.CleanUpSnapshots) {
    return super.cleanUpSnapshots(state, action);
  }

  //</editor-fold>

  @Action(FlatActions.ImageEntityActions.SetImageIndex)
  setImageIndex({patchState}: StateContext<ImageEntityStateModel>,
                {imageIndex}: FlatActions.ImageEntityActions.SetImageIndex) {
    patchState({
      imageIndex
    });
  }

  @Action(FlatActions.ImageEntityActions.RearrangeImages)
  rearrangeImages({getState, dispatch}: StateContext<ImageEntityStateModel>,
                  {images}: FlatActions.ImageEntityActions.RearrangeImages) {
    // Get Entities
    const entities = structuredClone(getState().entities);
    
    // Iterate Through Images
    let sequenceNumber = 0;
    for (let i = 0; i < images.length; i++) {
      // Get Image
      const image = images[i];
      
      // Get Entity
      let entity;
      if (typeof image === 'string') {
        entity = entities[image];
      } else {
        entity = entities[image.id];
      }
      if (!entity) {
        continue;
      }
      
      // Update Entity
      entity.sequenceNumber = sequenceNumber + 1;
      
      // Dispatch Update
      dispatch(new FlatActions.ImageEntityActions.UpdateEntity(entity));
      
      // Increment
      sequenceNumber++;
    }
  }

  //</editor-fold>
}
